import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import io from 'socket.io-client';
import {
  getEntityComments,
  createEntityComment,
  updateEntityComment,
  deleteEntityComment,
} from '../../utils/entityCommentsApi';

/**
 * Real-time entity comments for Shape Up.
 *
 * One org-level socket is opened by EntityCommentsProvider (mounted once, high
 * on the ShapeUp page) and shared by every CommentThread on the page. Each
 * thread's useEntityComments hook subscribes to just its own (type, id) so a
 * comment on one scope does not re-render every other thread.
 *
 * The socket carries the same serialized payloads the REST API returns, so a
 * single upsert/remove code path handles both local mutations and events that
 * arrive from other users (or from the inbound Jira sync).
 */

const EntityCommentsSocketContext = createContext(null);

const getServerUrl = () => {
  if (process.env.NODE_ENV === 'production') {
    return process.env.REACT_APP_SOCKET_URL || window.location.origin;
  }
  return 'http://localhost:5000';
};

/**
 * Opens the authenticated `org:<id>` socket room and fans incoming
 * entity-comment events out to per-entity subscribers. Renders children
 * regardless of connection state — comments still load and post over REST when
 * the socket is down; only live updates are lost.
 */
export function EntityCommentsProvider({ organizationId, refreshSignal = 0, children }) {
  const listenersRef = useRef(new Map()); // `${type}:${id}` -> Set<callback>
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    if (!organizationId) return undefined;
    const token = localStorage.getItem('authToken');
    if (!token) return undefined;

    const socket = io(getServerUrl(), {
      transports: ['websocket', 'polling'],
      withCredentials: true,
    });

    socket.on('connect', () => {
      // Membership is re-checked server-side before the join is honoured.
      socket.emit('join-org-comments', { token, organizationId });
    });
    socket.on('org-comments-joined', () => setConnected(true));
    socket.on('disconnect', () => setConnected(false));

    const fanOut = (event) => (payload) => {
      const key = `${payload.commentableType}:${payload.commentableId}`;
      const set = listenersRef.current.get(key);
      if (set) set.forEach((cb) => cb(event, payload));
    };
    const onAdded = fanOut('added');
    const onUpdated = fanOut('updated');
    const onDeleted = fanOut('deleted');
    socket.on('entity-comment-added', onAdded);
    socket.on('entity-comment-updated', onUpdated);
    socket.on('entity-comment-deleted', onDeleted);

    return () => {
      socket.emit('leave-org-comments', { organizationId });
      socket.disconnect();
      setConnected(false);
    };
  }, [organizationId]);

  const subscribe = useCallback((type, id, cb) => {
    const key = `${type}:${id}`;
    if (!listenersRef.current.has(key)) listenersRef.current.set(key, new Set());
    listenersRef.current.get(key).add(cb);
    return () => {
      const set = listenersRef.current.get(key);
      if (!set) return;
      set.delete(cb);
      if (set.size === 0) listenersRef.current.delete(key);
    };
  }, []);

  // A monotonic tick the page bumps after an explicit Jira sync. Threads watch
  // it and re-fetch, so a sync reflects the pulled-in discussion even when the
  // socket is down or the broadcast is missed (the thread was collapsed).
  const value = useMemo(
    () => ({ subscribe, connected, organizationId, refreshSignal }),
    [subscribe, connected, organizationId, refreshSignal],
  );

  return (
    <EntityCommentsSocketContext.Provider value={value}>
      {children}
    </EntityCommentsSocketContext.Provider>
  );
}

/**
 * The org the surrounding EntityCommentsProvider is scoped to. Lets a
 * CommentThread deep in the tree author comments without every intermediate
 * component having to forward organizationId.
 */
export function useCommentsOrganizationId() {
  return useContext(EntityCommentsSocketContext)?.organizationId || null;
}

// ---- pure list reducers (shared by local ops and socket events) ----------

function byCreatedAt(a, b) {
  return new Date(a.createdAt) - new Date(b.createdAt);
}

/**
 * Insert or replace a comment in the threaded list. Top-level comments live at
 * the root; replies live in their parent's `replies` array. Matching by id
 * makes this idempotent, so the author's optimistic insert and the socket echo
 * that follows collapse into one entry.
 */
function upsertComment(list, comment) {
  if (comment.parentCommentId) {
    return list.map((top) => {
      if (top.id !== comment.parentCommentId) return top;
      const replies = top.replies ? [...top.replies] : [];
      const idx = replies.findIndex((r) => r.id === comment.id);
      if (idx === -1) replies.push(comment);
      else replies[idx] = comment;
      replies.sort(byCreatedAt);
      return { ...top, replies };
    });
  }

  const idx = list.findIndex((c) => c.id === comment.id);
  const next = [...list];
  // Preserve any replies we already hold if the incoming top-level payload
  // omits them (socket edits carry the comment alone).
  const merged = idx === -1
    ? comment
    : { ...comment, replies: comment.replies ?? next[idx].replies ?? [] };
  if (idx === -1) next.push(merged);
  else next[idx] = merged;
  next.sort(byCreatedAt);
  return next;
}

function removeComment(list, commentId) {
  if (list.some((c) => c.id === commentId)) {
    return list.filter((c) => c.id !== commentId);
  }
  return list.map((top) => (
    top.replies?.some((r) => r.id === commentId)
      ? { ...top, replies: top.replies.filter((r) => r.id !== commentId) }
      : top
  ));
}

// ---- per-thread hook -----------------------------------------------------

/**
 * Load + live-update the comment thread for a single entity, and expose
 * create/edit/remove actions.
 *
 * @param {'scope'|'bet'|'pitch'} commentableType
 * @param {string} commentableId
 * @param {string} organizationId - required to author comments
 */
export function useEntityComments(commentableType, commentableId, organizationId) {
  const ctx = useContext(EntityCommentsSocketContext);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!commentableType || !commentableId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getEntityComments(commentableType, commentableId);
      setComments(data);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load comments');
    } finally {
      setLoading(false);
    }
  }, [commentableType, commentableId]);

  useEffect(() => {
    load();
  }, [load]);

  // Re-fetch when the page signals a sync just completed. The initial signal
  // value is folded into the mount load above, so only later bumps refetch.
  const lastSignalRef = useRef(ctx?.refreshSignal ?? 0);
  useEffect(() => {
    const signal = ctx?.refreshSignal ?? 0;
    if (signal === lastSignalRef.current) return;
    lastSignalRef.current = signal;
    load();
  }, [ctx?.refreshSignal, load]);

  useEffect(() => {
    if (!ctx || !commentableType || !commentableId) return undefined;
    return ctx.subscribe(commentableType, commentableId, (event, payload) => {
      setComments((prev) => {
        if (event === 'deleted') return removeComment(prev, payload.commentId);
        return upsertComment(prev, payload.comment);
      });
    });
  }, [ctx, commentableType, commentableId]);

  const create = useCallback(async (content, parentCommentId = null) => {
    const created = await createEntityComment({
      commentableType,
      commentableId,
      content,
      organizationId,
      parentCommentId,
    });
    // Reflect immediately; the socket echo will upsert to the same id.
    setComments((prev) => upsertComment(prev, created));
    return created;
  }, [commentableType, commentableId, organizationId]);

  const edit = useCallback(async (commentId, content) => {
    const updated = await updateEntityComment(commentId, content);
    setComments((prev) => upsertComment(prev, updated));
    return updated;
  }, []);

  const remove = useCallback(async (commentId) => {
    await deleteEntityComment(commentId);
    setComments((prev) => removeComment(prev, commentId));
  }, []);

  const count = useMemo(
    () => comments.reduce((sum, c) => sum + 1 + (c.replies?.length || 0), 0),
    [comments],
  );

  return {
    comments,
    count,
    loading,
    error,
    connected: !!ctx?.connected,
    reload: load,
    create,
    edit,
    remove,
  };
}

export default useEntityComments;
